import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import moment from 'moment';
import { util as sdkUtil } from '../util/sdkLoader';
import { denormalisedResponseEntities, ensureOwnListing } from '../util/data';
import * as log from '../util/log';
import { identifyKlaviyoUser } from '../util/klaviyo';
import { LISTING_STATE_DRAFT } from '../util/types';
import { storableError, isNetworkError, isForbiddenError } from '../util/errors';
import { filterVisibleTransactions } from '../util/transactionVisibility';
import { isUserAuthorized } from '../util/userHelpers';
import {
  createParticipantUnavailableError,
  splitTransactionsByParticipantAvailability,
  unavailableParticipantLogData,
} from '../util/participantAvailability';
import {
  getStatesNeedingProviderAttention,
  getStatesNeedingCustomerAttention,
} from '../transactions/transaction';

import { authInfo } from './auth.duck';
import { createLastActivityTimestamp } from '../util/authHelpers';
import { recordReferralPriorActivity } from '../util/tapfiliate';
import { returnFromAutoVacation as returnFromAutoVacationApi } from '../util/api';
import { updateStripeConnectAccount } from './stripeConnectAccount.duck';

// ================ Helper Functions ================ //

const mergeCurrentUser = (oldCurrentUser, newCurrentUser) => {
  const { id: oId, type: oType, attributes: oAttr, ...oldRelationships } = oldCurrentUser || {};
  const { id, type, attributes, ...relationships } = newCurrentUser || {};

  // Passing null will remove currentUser entity.
  // Only relationships are merged.
  // TODO figure out if sparse fields handling needs a better handling.
  return newCurrentUser === null
    ? null
    : oldCurrentUser === null
    ? newCurrentUser
    : { id, type, attributes, ...oldRelationships, ...relationships };
};

// ================ Async Thunks ================ //

//////////////////////////////////////////////////////////////////////
// Fetch ownListings to check if currentUser has published listings //
//////////////////////////////////////////////////////////////////////

const fetchCurrentUserHasListingsPayloadCreator = (_, thunkAPI) => {
  const { getState, extra: sdk, rejectWithValue } = thunkAPI;
  const { currentUser } = getState().user;

  if (!currentUser) {
    return Promise.resolve({ hasListings: false });
  }

  const params = {
    // Since we are only interested in if the user has published
    // listings, we only need at most one result.
    states: 'published',
    page: 1,
    perPage: 1,
  };

  return sdk.ownListings
    .query(params)
    .then(response => {
      const hasListings = response.data.data && response.data.data.length > 0;

      const hasPublishedListings =
        hasListings &&
        ensureOwnListing(response.data.data[0]).attributes.state !== LISTING_STATE_DRAFT;
      return { hasListings: !!hasPublishedListings };
    })
    .catch(e => rejectWithValue(storableError(e)));
};

export const fetchCurrentUserHasListingsThunk = createAsyncThunk(
  'user/fetchCurrentUserHasListings',
  fetchCurrentUserHasListingsPayloadCreator
);

// Backward compatible wrapper for the thunk
export const fetchCurrentUserHasListings = () => (dispatch, getState, sdk) => {
  return dispatch(fetchCurrentUserHasListingsThunk()).unwrap();
};

///////////////////////////////////////////////////////////
// Fetch transactions to check if currentUser has orders //
///////////////////////////////////////////////////////////

const fetchCurrentUserHasOrdersPayloadCreator = (_, { getState, extra: sdk, rejectWithValue }) => {
  if (!getState().user.currentUser) {
    return Promise.resolve({ hasOrders: false });
  }

  const params = {
    only: 'order',
    page: 1,
    perPage: 1,
  };

  return sdk.transactions
    .query(params)
    .then(response => {
      const transactions = response?.data?.data || [];
      const visibleTransactions = filterVisibleTransactions(transactions);
      const totalItems = Number(response?.data?.meta?.totalItems);
      const blockedTransactionsOnPage = transactions.length - visibleTransactions.length;
      const hasOrders =
        visibleTransactions.length > 0 ||
        (Number.isFinite(totalItems) && totalItems > blockedTransactionsOnPage);
      return { hasOrders: !!hasOrders };
    })
    .catch(e => rejectWithValue(storableError(e)));
};

export const fetchCurrentUserHasOrdersThunk = createAsyncThunk(
  'user/fetchCurrentUserHasOrders',
  fetchCurrentUserHasOrdersPayloadCreator
);

// Backward compatible wrapper for the thunk
export const fetchCurrentUserHasOrders = () => (dispatch, getState, sdk) => {
  return dispatch(fetchCurrentUserHasOrdersThunk()).unwrap();
};

/////////////////////////////////////////////////////////////////////////////////////
// Fetch transactions in specific states to check if currentUser has notifications //
/////////////////////////////////////////////////////////////////////////////////////

// Notificaiton page size is max (100 items on page)
const NOTIFICATION_PAGE_SIZE = 100;
const NOTIFICATION_USER_FIELDS = [
  'profile.displayName',
  'profile.abbreviatedName',
  'deleted',
  'banned',
  'state',
  'disabled',
];

const countAvailableNotificationTransactions = response => {
  const rawTransactions = filterVisibleTransactions(response?.data?.data || []);
  let denormalizedTransactions = [];

  try {
    denormalizedTransactions = denormalisedResponseEntities(response);
  } catch (e) {
    log.warn(e, 'notification-participant-denormalization-failed', {
      totalItems: response?.data?.meta?.totalItems || null,
    });
  }

  const { visibleTransactions, hiddenTransactions } = splitTransactionsByParticipantAvailability(
    rawTransactions,
    denormalizedTransactions,
    response?.data?.included || []
  );

  hiddenTransactions.forEach(({ transaction, unavailableParticipant }) => {
    log.warn(
      createParticipantUnavailableError(),
      'notification-hidden-unavailable-participant',
      unavailableParticipantLogData(transaction, unavailableParticipant)
    );
  });

  return visibleTransactions.length;
};

const fetchCurrentUserNotificationsPayloadCreator = (_, { extra: sdk, rejectWithValue }) => {
  const statesNeedingProviderAttention = getStatesNeedingProviderAttention() || [];
  const statesNeedingCustomerAttention = getStatesNeedingCustomerAttention() || [];

  const paramsForSales = {
    only: 'sale',
    states: statesNeedingProviderAttention.map(state => `state/${state}`).join(','),
    page: 1,
    perPage: NOTIFICATION_PAGE_SIZE,
    include: ['customer', 'provider'],
    'fields.user': NOTIFICATION_USER_FIELDS,
  };
  const paramsForOrders = {
    only: 'order',
    states: statesNeedingCustomerAttention.map(state => `state/${state}`).join(','),
    page: 1,
    perPage: NOTIFICATION_PAGE_SIZE,
    include: ['customer', 'provider'],
    'fields.user': NOTIFICATION_USER_FIELDS,
  };

  const MAX_RETRIES = 2;
  const queryWithRetry = async (params, retriesLeft) => {
    try {
      return await sdk.transactions.query(params);
    } catch (e) {
      if (retriesLeft > 0) {
        await new Promise(resolve => setTimeout(resolve, 1000));
        return queryWithRetry(params, retriesLeft - 1);
      }
      throw e;
    }
  };

  return Promise.all([
    queryWithRetry(paramsForSales, MAX_RETRIES),
    queryWithRetry(paramsForOrders, MAX_RETRIES),
  ])
    .then(([sales, orders]) => {
      const saleNotificationsCount = countAvailableNotificationTransactions(sales);
      const orderNotificationsCount = countAvailableNotificationTransactions(orders);
      return { saleNotificationsCount, orderNotificationsCount };
    })
    .catch(e => rejectWithValue(storableError(e)));
};

export const fetchCurrentUserNotificationsThunk = createAsyncThunk(
  'user/fetchCurrentUserNotifications',
  fetchCurrentUserNotificationsPayloadCreator
);

// Backward compatible wrapper for the thunk
export const fetchCurrentUserNotifications = () => (dispatch, getState, sdk) => {
  return dispatch(fetchCurrentUserNotificationsThunk()).unwrap();
};

const NOTIFICATIONS_MAX_AGE_MS = 60 * 1000;
const HAS_LISTINGS_MAX_AGE_MS = 5 * 60 * 1000;

const fetchCurrentUserPayloadCreator = (options, thunkAPI) => {
  const { getState, dispatch, extra: sdk, rejectWithValue } = thunkAPI;
  const state = getState();
  const {
    currentUserHasListings,
    currentUserShowTimestamp,
    currentUserNotificationsFetchedAt = 0,
    currentUserHasListingsCheckedAt = 0,
  } = state.user || {};
  const { isAuthenticated } = state.auth;
  const {
    callParams = null,
    updateHasListings = true,
    updateNotifications = true,
    afterLogin,
    enforce = false, // Automatic emailVerification might be called too fast
  } = options || {};

  // Double fetch might happen when e.g. profile page is making a full page load
  const aSecondAgo = new Date().getTime() - 1000;
  if (!enforce && currentUserShowTimestamp > aSecondAgo) {
    return Promise.resolve(state.user.currentUser);
  }

  if (!isAuthenticated && !afterLogin) {
    // Make sure current user is null
    return Promise.resolve(null);
  }

  const parameters = callParams || {
    include: ['effectivePermissionSet', 'profileImage', 'stripeAccount'],
    'fields.image': [
      'variants.square-small',
      'variants.square-small2x',
      'variants.square-xsmall',
      'variants.square-xsmall2x',
    ],
    'imageVariant.square-xsmall': sdkUtil.objectQueryString({
      w: 40,
      h: 40,
      fit: 'crop',
    }),
    'imageVariant.square-xsmall2x': sdkUtil.objectQueryString({
      w: 80,
      h: 80,
      fit: 'crop',
    }),
  };

  const MAX_RETRIES = 2;
  const showWithRetry = async retriesLeft => {
    try {
      return await sdk.currentUser.show(parameters);
    } catch (e) {
      if (retriesLeft > 0 && isNetworkError(e)) {
        await new Promise(resolve => setTimeout(resolve, 1000));
        return showWithRetry(retriesLeft - 1);
      }
      throw e;
    }
  };

  return showWithRetry(MAX_RETRIES)
    .then(response => {
      const entities = denormalisedResponseEntities(response);
      if (entities.length !== 1) {
        throw new Error('Expected a resource in the sdk.currentUser.show response');
      }
      const currentUser = entities[0];

      // Save stripeAccount to store.stripe.stripeAccount if it exists
      if (currentUser.stripeAccount) {
        dispatch(updateStripeConnectAccount(currentUser.stripeAccount));
      }

      // set current user id to the logger
      log.setUserId(currentUser.id.uuid);

      // Identify the user to Klaviyo so onsite activity ties to their existing
      // profile. No-ops when the Klaviyo SDK isn't configured.
      identifyKlaviyoUser(currentUser);

      return currentUser;
    })
    .then(currentUser => {
      // If currentUser is not active (e.g. in 'pending-approval' state),
      // then they don't have listings or transactions that we care about.
      if (isUserAuthorized(currentUser)) {
        // Every page asks for the current user; the order counts and the has-listings check are
        // billed searches, so moving between pages reuses recent ones. Pages that change an
        // order fetch the counts again themselves.
        const now = new Date().getTime();
        const hasListingsIsRecent = now - currentUserHasListingsCheckedAt < HAS_LISTINGS_MAX_AGE_MS;
        const notificationsAreRecent =
          now - currentUserNotificationsFetchedAt < NOTIFICATIONS_MAX_AGE_MS;

        if (
          currentUserHasListings === false &&
          updateHasListings !== false &&
          (enforce || !hasListingsIsRecent)
        ) {
          dispatch(fetchCurrentUserHasListings());
        }

        if (updateNotifications !== false && (enforce || !notificationsAreRecent)) {
          dispatch(fetchCurrentUserNotifications());
        }

        if (!currentUser.attributes.emailVerified) {
          dispatch(fetchCurrentUserHasOrders());
        }
      }

      // Make sure auth info is up to date
      dispatch(authInfo());
      return currentUser;
    })
    .catch(e => {
      // Make sure auth info is up to date
      dispatch(authInfo());
      const isNetError = isNetworkError(e);
      if (!isForbiddenError(e)) {
        const logFn = isNetError ? log.warn : log.error;
        logFn(e, 'fetch-current-user-failed');
      }

      return rejectWithValue(storableError(e));
    });
};

export const fetchCurrentUserThunk = createAsyncThunk(
  'user/fetchCurrentUser',
  fetchCurrentUserPayloadCreator
);
// Backward compatible wrapper for the thunk
/**
 * Fetch currentUser API entity.
 *
 * @param {Object} options
 * @param {Object} [options.callParams]           Optional parameters for the currentUser.show().
 * @param {boolean} [options.updateHasListings]   Make extra call for fetchCurrentUserHasListings()?
 * @param {boolean} [options.updateNotifications] Make extra call for fetchCurrentUserNotifications()?
 * @param {boolean} [options.afterLogin]          Fetch is no-op for unauthenticated users except after login() call
 * @param {boolean} [options.enforce]             Enforce the call even if the currentUser entity is freshly fetched.
 */
export const fetchCurrentUser = options => (dispatch, getState, sdk) => {
  return dispatch(fetchCurrentUserThunk(options)).unwrap();
};

////////////////////////////////////////////////////
// Reopen a shop paused for inactivity on return //
////////////////////////////////////////////////////

// Auto-vacation (server/auto-vacation.js) pauses inactive sellers' shops. Coming back to the site
// reopens it; a vacation the seller turned on themselves stays on.
export const returnFromAutoVacation = () => (dispatch, getState) => {
  const state = getState();
  const currentUser = state.user?.currentUser;
  const privateData = currentUser?.attributes?.profile?.privateData || {};
  // Console "Log in as user" must not reopen the shop
  if (
    !currentUser?.id ||
    state.auth?.isLoggedInAs ||
    privateData.vacationMode !== true ||
    privateData.vacationModeSetBy !== 'auto'
  ) {
    return Promise.resolve(false);
  }
  return returnFromAutoVacationApi({})
    .then(response => {
      if (!response?.reopened) {
        return false;
      }
      dispatch(setAutoVacationReopened(true));
      return dispatch(fetchCurrentUser()).then(() => true);
    })
    .catch(e => {
      log.error(e, 'return-from-auto-vacation-failed');
      return false;
    });
};

//////////////////////////////////////////////////
// Refresh lastActivity when a signed-in user visits //
//////////////////////////////////////////////////

// Login only writes lastActivity on a password login, and most users stay logged in, so it went
// stale. Refresh it on page load, at most every few hours. Used for the store page "Active" label,
// affiliate eligibility and inactivity checks.
const LAST_ACTIVITY_REFRESH_MS = 6 * 60 * 60 * 1000;

export const shouldRefreshLastActivity = (lastActivity, now = Date.now()) => {
  if (!lastActivity) {
    return true;
  }
  const last = moment(lastActivity);
  return !last.isValid() || now - last.valueOf() >= LAST_ACTIVITY_REFRESH_MS;
};

export const refreshLastActivity = () => (dispatch, getState, sdk) => {
  const state = getState();
  const currentUser = state.user?.currentUser;
  // Console "Log in as user" must not make the user look active
  if (!currentUser?.id || state.auth?.isLoggedInAs) {
    return Promise.resolve(null);
  }
  const lastActivity = currentUser.attributes?.profile?.publicData?.lastActivity;
  if (!shouldRefreshLastActivity(lastActivity)) {
    return Promise.resolve(null);
  }

  // Affiliate eligibility needs the value from before this visit (no-op without a referral)
  recordReferralPriorActivity(currentUser);
  return sdk.currentUser
    .updateProfile({ publicData: { lastActivity: createLastActivityTimestamp() } })
    .catch(e => {
      // 409 is a concurrent profile update; the next visit will refresh it
      if (e?.status !== 409) {
        log.error(e, 'refresh-last-activity-failed');
      }
      return null;
    });
};

/////////////////////////////////////////////
// Send verification email to currentUser //
/////////////////////////////////////////////

const sendVerificationEmailPayloadCreator = (_, { extra: sdk, rejectWithValue }) => {
  return sdk.currentUser
    .sendVerificationEmail()
    .then(() => ({}))
    .catch(e => rejectWithValue(storableError(e)));
};
export const sendVerificationEmailThunk = createAsyncThunk(
  'user/sendVerificationEmail',
  sendVerificationEmailPayloadCreator,
  {
    condition: (_, { getState }) => {
      return !getState()?.user?.sendVerificationEmailInProgress;
    },
  }
);

// Backward compatible wrapper for the thunk
export const sendVerificationEmail = () => (dispatch, getState, sdk) => {
  return dispatch(sendVerificationEmailThunk()).unwrap();
};

// ================ Slice ================ //

const userSlice = createSlice({
  name: 'user',
  initialState: {
    currentUser: null,
    currentUserShowTimestamp: 0,
    currentUserShowError: null,
    currentUserHasListings: false,
    currentUserHasListingsError: null,
    currentUserSaleNotificationCount: 0,
    currentUserOrderNotificationCount: 0,
    currentUserNotificationCountError: null,
    // When the counts above and currentUserHasListings were last fetched (ms)
    currentUserNotificationsFetchedAt: 0,
    currentUserHasListingsCheckedAt: 0,
    currentUserHasOrders: null, // This is not fetched unless unverified emails exist
    currentUserHasOrdersError: null,
    sendVerificationEmailInProgress: false,
    sendVerificationEmailError: null,
    // True right after the site reopened a shop that auto-vacation had paused
    autoVacationReopened: false,
  },
  reducers: {
    setAutoVacationReopened: (state, action) => {
      state.autoVacationReopened = action.payload;
    },
    clearCurrentUser: state => {
      state.currentUser = null;
      state.currentUserShowError = null;
      state.currentUserHasListings = false;
      state.currentUserHasListingsError = null;
      state.currentUserSaleNotificationCount = 0;
      state.currentUserOrderNotificationCount = 0;
      state.currentUserNotificationsFetchedAt = 0;
      state.currentUserHasListingsCheckedAt = 0;

      state.currentUserNotificationCountError = null;
    },
    setCurrentUser: (state, action) => {
      state.currentUser = mergeCurrentUser(state.currentUser, action.payload);
    },
    setCurrentUserHasOrders: state => {
      state.currentUserHasOrders = true;
    },
  },
  extraReducers: builder => {
    builder
      // A seller who just published has listings (the Shop link shouldn't wait for a re-check)
      .addCase('EditListingPage/publishListing/fulfilled', state => {
        state.currentUserHasListings = true;
      })
      // fetchCurrentUser
      .addCase(fetchCurrentUserThunk.pending, state => {
        state.currentUserShowError = null;
      })
      .addCase(fetchCurrentUserThunk.fulfilled, (state, action) => {
        state.currentUser = mergeCurrentUser(state.currentUser, action.payload);
        state.currentUserShowTimestamp = action.payload ? new Date().getTime() : 0;
      })
      .addCase(fetchCurrentUserThunk.rejected, (state, action) => {
        // eslint-disable-next-line no-console
        console.error(action.payload);
        state.currentUserShowError = action.payload;
      })
      // fetchCurrentUserHasListings
      .addCase(fetchCurrentUserHasListingsThunk.pending, state => {
        state.currentUserHasListingsError = null;
      })
      .addCase(fetchCurrentUserHasListingsThunk.fulfilled, (state, action) => {
        state.currentUserHasListings = action.payload.hasListings;
        state.currentUserHasListingsCheckedAt = new Date().getTime();
      })
      .addCase(fetchCurrentUserHasListingsThunk.rejected, (state, action) => {
        console.error(action.payload); // eslint-disable-line
        state.currentUserHasListingsError = action.payload;
      })
      // fetchCurrentUserNotifications
      .addCase(fetchCurrentUserNotificationsThunk.pending, state => {
        state.currentUserNotificationCountError = null;
      })
      .addCase(fetchCurrentUserNotificationsThunk.fulfilled, (state, action) => {
        state.currentUserSaleNotificationCount = action.payload.saleNotificationsCount;
        state.currentUserOrderNotificationCount = action.payload.orderNotificationsCount;
        state.currentUserNotificationsFetchedAt = new Date().getTime();
      })
      .addCase(fetchCurrentUserNotificationsThunk.rejected, (state, action) => {
        console.error(action.payload); // eslint-disable-line
        state.currentUserNotificationCountError = action.payload;
      })
      // fetchCurrentUserHasOrders
      .addCase(fetchCurrentUserHasOrdersThunk.pending, state => {
        state.currentUserHasOrdersError = null;
      })
      .addCase(fetchCurrentUserHasOrdersThunk.fulfilled, (state, action) => {
        state.currentUserHasOrders = action.payload.hasOrders;
      })
      .addCase(fetchCurrentUserHasOrdersThunk.rejected, (state, action) => {
        console.error(action.payload); // eslint-disable-line
        state.currentUserHasOrdersError = action.payload;
      })
      // sendVerificationEmail
      .addCase(sendVerificationEmailThunk.pending, state => {
        state.sendVerificationEmailInProgress = true;
        state.sendVerificationEmailError = null;
      })
      .addCase(sendVerificationEmailThunk.fulfilled, state => {
        state.sendVerificationEmailInProgress = false;
      })
      .addCase(sendVerificationEmailThunk.rejected, (state, action) => {
        state.sendVerificationEmailInProgress = false;
        state.sendVerificationEmailError = action.payload;
      });
  },
});

export default userSlice.reducer;

export const {
  clearCurrentUser,
  setCurrentUser,
  setCurrentUserHasOrders,
  setAutoVacationReopened,
} = userSlice.actions;

// ================ Selectors ================ //

export const hasCurrentUserErrors = state => {
  const { user } = state;
  return (
    user.currentUserShowError ||
    user.currentUserHasListingsError ||
    user.currentUserNotificationCountError ||
    user.currentUserHasOrdersError
  );
};
